'use client';

import { useLayoutEffect, useMemo, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImageWithFallback from '@/components/ui/ImageWithFallback';
import { ProductInnerListItem, ImageType } from '@/types/product';

const defaultBenefits: ProductInnerListItem[] = [
  {
    id: 1,
    listTitle: 'Achieve Sustained Strength',
    listContent:
      'Unlike standard variants, Wonder PPC continues to gain strength long after the initial pour.',
  },
  {
    id: 2,
    listTitle: 'Defend Against Corrosion',
    listContent:
      'Engineered to exceed PPC cement Indian Standards, our cement offers exceptional resistance.',
  },
  {
    id: 3,
    listTitle: 'Build Sustainably',
    listContent: 'Make an eco-friendly choice without compromising on performance.',
  },
  {
    id: 4,
    listTitle: 'Ensure a Crack-Resistant Finish',
    listContent: 'Minimise the risk of thermal cracks.',
  },
  {
    id: 5,
    listTitle: 'Prevent Structural Expansion',
    listContent: 'By maintaining a strictly controlled low alkali content.',
  },
  {
    id: 6,
    listTitle: 'Safeguard Reinforcement',
    listContent: 'Our manufacturing process ensures minimal chloride levels.',
  },
];

interface ProductPpcBenefitsProps {
  heading?: string;
  subHeading?: string;
  media?: ImageType;
  detailMedia?: ImageType;
  benefitsList?: ProductInnerListItem[];
  showHeader?: boolean;
}

function getTitleLines(title: string) {
  const words = title.trim().split(/\s+/).filter(Boolean);
  if (words.length <= 4) return words;

  const midpoint = Math.ceil(words.length / 2);
  return [words.slice(0, midpoint).join(' '), words.slice(midpoint).join(' ')];
}

export default function ProductPpcBenefits({
  heading = 'Benefits of Wonder PPC Cement',
  subHeading = "Built for India's demanding climates, Wonder PPC offers a multi-layered shield of chemical resistance, sustained strength, and eco-friendly durability.",
  media,
  detailMedia,
  benefitsList,
  showHeader = true,
}: ProductPpcBenefitsProps) {
  const sectionRef = useRef<HTMLElement | null>(null);
  const titleLines = useMemo(() => getTitleLines(heading), [heading]);
  const displayBenefits = benefitsList ?? defaultBenefits;
  const showMediaColumn = Boolean(media || detailMedia);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      const titleLines = gsap.utils.toArray<HTMLElement>('[data-benefits-title-line]');
      const introEls = gsap.utils.toArray<HTMLElement>('[data-benefits-intro]');
      const features = gsap.utils.toArray<HTMLElement>('[data-benefits-feature]');
      const mediaFrames = gsap.utils.toArray<HTMLElement>('[data-benefits-media]');
      const parallaxImages = gsap.utils.toArray<HTMLElement>('[data-benefits-parallax]');
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (prefersReducedMotion) {
        gsap.set([...titleLines, ...introEls, ...features, ...mediaFrames], { clearProps: 'all' });
        return;
      }

      gsap.set(titleLines, { yPercent: 105 });
      gsap.set(introEls, { autoAlpha: 0, x: -24 });
      gsap.set(features, { autoAlpha: 0, y: 34 });
      gsap.set(mediaFrames, { clipPath: 'inset(100% 0% 0% 0%)' });

      gsap
        .timeline({
          scrollTrigger: {
            trigger: section,
            start: 'top 78%',
            once: true,
          },
        })
        .to(introEls, {
          autoAlpha: 1,
          x: 0,
          duration: 0.75,
          stagger: 0.08,
          ease: 'power3.out',
        })
        .to(
          titleLines,
          {
            yPercent: 0,
            duration: 1,
            stagger: 0.08,
            ease: 'expo.out',
          },
          '<0.08',
        )
        .to(
          features,
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.65,
            stagger: 0.09,
            ease: 'power2.out',
          },
          '<0.35',
        )
        .to(
          mediaFrames,
          {
            clipPath: 'inset(0% 0% 0% 0%)',
            duration: 1.25,
            stagger: 0.16,
            ease: 'expo.inOut',
          },
          '<0.05',
        );

      parallaxImages.forEach((image, index) => {
        gsap.to(image, {
          y: index === 0 ? -52 : 34,
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 1.5 + index * 0.4,
          },
        });
      });
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      data-page-reveal="false"
      className="relative overflow-hidden bg-[#f0f0f0] text-[#0c0c0b]"
    >
      <div aria-hidden className="absolute inset-0" />
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-0">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-[minmax(0,1fr)_460px] lg:gap-20">
          <div>
            {showHeader && (
              <>
                <h2 className="max-w-[680px] overflow-hidden text-3xl font-semibold tracking-tight text-[#0c0c0b] md:text-4xl lg:text-[2.5rem] lg:leading-tight">
                  {titleLines.map((line, index) => (
                    <span key={index} className="block overflow-hidden">
                      <span data-benefits-title-line className="block">
                        {line}
                      </span>
                    </span>
                  ))}
                </h2>
                {subHeading && (
                  <div
                    data-benefits-intro
                    className="prose prose-gray mt-4 mb-8 max-w-2xl text-base leading-relaxed text-[#0c0c0b]/70 md:mb-10 md:text-lg"
                    dangerouslySetInnerHTML={{ __html: subHeading }}
                  />
                )}
              </>
            )}

            <div className={`grid grid-cols-1 sm:grid-cols-2 ${showHeader ? '' : 'lg:pt-2'}`}>
              {displayBenefits.map((benefit, index) => (
                <div
                  key={benefit.id}
                  data-benefits-feature
                  className="border-t border-[#0c0c0b]/15 py-7 sm:pr-10 first:border-t-0 [&:nth-child(2)]:border-t-0"
                >
                  <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.26em] text-red-600">
                    — {String(index + 1).padStart(2, '0')}
                  </div>
                  <h3 className="mb-2 text-lg font-semibold tracking-[-0.01em] text-[#0c0c0b]">
                    {benefit.listTitle}
                  </h3>
                  <div
                    className="prose prose-sm max-w-none text-sm leading-[1.75] text-[#0c0c0b]/55"
                    dangerouslySetInnerHTML={{ __html: benefit.listContent }}
                  />
                </div>
              ))}
            </div>
          </div>

          {showMediaColumn ? (
            <div className="relative hidden lg:block">
              <div className="relative h-[580px]">
                {media ? (
                  <div
                    data-benefits-media
                    className="absolute left-0 top-0 h-auto w-[85%] overflow-hidden bg-neutral-200 rounded-md"
                    style={{ aspectRatio: '3 / 4' }}
                  >
                    <div data-benefits-parallax className="absolute inset-0 h-[115%]">
                      <ImageWithFallback
                        image={media}
                        alt={media.alternativeText || 'Product benefits image'}
                        fill
                        sizes="390px"
                        className="object-cover"
                        fallbackSrc="/images/product-img.jpg"
                      />
                    </div>
                  </div>
                ) : null}
                {detailMedia ? (
                  <div
                    data-benefits-media
                    className={`absolute bottom-0 right-0 w-[45%] overflow-hidden rounded-md ${
                      media ? '' : 'left-0 top-0 w-[85%]'
                    }`}
                    style={{ aspectRatio: media ? '1 / 1.35' : '3 / 4' }}
                  >
                    <div
                      data-benefits-parallax
                      className="absolute inset-0 h-[100%] overflow-hidden rounded-md"
                    >
                      <ImageWithFallback
                        image={detailMedia}
                        alt={detailMedia.alternativeText || 'Product benefits detail image'}
                        fill
                        className="rounded-md object-cover"
                        sizes="260px"
                        fallbackSrc="/images/product-img.jpg"
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
